import type { PricingList } from 'api/types/pricing.types';
import { CollapseIcon, PlusIcon } from 'assets/icons';
import cx from 'classnames';
import Button from 'components/atoms/Button';
import { BUTTON_NAMES } from 'constant/button-names';
import React from 'react';
import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { CartItem } from 'store/cart-estimator';
import styled from 'styled-components';

interface Props {
  item: CartItem;
  add: (item: CartItem, serviceType: PricingList.ServiceTypes) => any;
  remove: (item: CartItem, serviceType: PricingList.ServiceTypes) => any;
  isDisabled?: boolean;
  serviceType: PricingList.ServiceTypes;
  customColor?: string;
}

const StyledAddButton = styled(Button)<{ amount: number; customColor?: string }>`
  width: 80px;
  height: 32px;
  border-radius: 6px;
  background-color: ${({ theme, amount, customColor }) =>
    amount > 0 ? (customColor ? '#FFE7FF' : theme.components.pages.pricing?.button?.hover?.active) : '#fff'};
  font-weight: 400;
  border-width: 1px;
  padding: 0px;
  color: ${({ theme, customColor, amount }) =>
    amount > 0 ? theme.components.pages.pricing?.button?.color?.active : customColor || theme.components.pages.pricing?.button?.color?.inactive};
  border-color: ${({ theme, customColor, amount }) =>
    amount > 0
      ? theme.components.pages.pricing?.button?.borderColor.active
      : customColor || theme.components.pages.pricing?.button?.borderColor.inactive};
  border-radius: 1000px;

  &:hover,
  &:focus,
  &:active,
  &.btn-primary:not(:disabled):not(.disabled):active {
    color: ${({ theme, customColor }) => customColor || theme.text.success};
    background-color: ${({ customColor, theme }) => (customColor ? '#FFE7FF' : theme.components.pages.pricing?.button?.hover?.active)};
    border-width: 1px;
    border-color: ${({ theme, customColor, amount }) =>
      amount > 0
        ? theme.components.pages.pricing?.button?.borderColor.active
        : customColor || theme.components.pages.pricing?.button?.borderColor.inactive};
  }

  &:disabled {
    background-color: #f3f8fb;
    border-width: 1px;
    border-color: #e9edf1;
    color: #889db4;
  }
`;

const StyledButton = styled(Button)`
  border-color: transparent;
  background-color: transparent;
  width: 40px;
  height: 32px;
  padding: 0px;

  &:hover,
  &:focus,
  &:active,
  &.btn-primary:not(:disabled):not(.disabled):active {
    border-color: transparent;
    background-color: transparent;
  }
`;

const StyledMinusIcon = styled(CollapseIcon)<{ customColor?: string }>`
  path {
    stroke: ${({ theme, customColor }) => customColor ?? theme.components.pages.pricing?.button?.arrow};
  }
`;

const StyledPlusIcon = styled(PlusIcon)<{ customColor?: string }>`
  path {
    fill: ${({ theme, customColor }) => customColor ?? theme.components.pages.pricing?.button?.arrow};
  }
`;

const AddButton: React.FC<Props> = ({ item, isDisabled = false, serviceType, add, remove, customColor }) => {
  const { t } = useTranslation();

  return (
    <StyledAddButton
      customColor={customColor}
      amount={item.quantity}
      className={cx('d-flex align-items-center justify-content-center', { disabled: isDisabled })}
      onClick={() => (item.quantity === 0 ? add(item, serviceType) : undefined)}
      disabled={isDisabled}
      buttonName={BUTTON_NAMES.ADD}
    >
      {item.quantity > 0 ? (
        <Col className="d-flex align-items-center justify-content-between p-0">
          <StyledButton buttonName={BUTTON_NAMES.REMOVE} onClick={() => remove(item, serviceType)}>
            <StyledMinusIcon customColor={customColor} />
          </StyledButton>
          <div>{item.quantity}</div>
          <StyledButton buttonName={BUTTON_NAMES.ADD} onClick={() => add(item, serviceType)}>
            <StyledPlusIcon customColor={customColor} />
          </StyledButton>
        </Col>
      ) : (
        <div>{t('common.Add', 'Add')}</div>
      )}
    </StyledAddButton>
  );
};

export default AddButton;
